/**
 * DoctorWebsiteMockA — visual-only mock at /dr/:slug/preview-a
 *
 * Goal: Zocdoc-functional density × DocSite-ownership angle × oversized
 * editorial typography. Not yet wired for bookings — purpose is to let the
 * founder evaluate the visual + structural redesign before we touch the
 * production /dr/:slug page.
 *
 * Reads the same /api/doctor/{slug} payload the production page uses, so
 * data shape is real. Anything not yet implemented (review feed, slots,
 * insurances list) is rendered as faithful placeholders.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import MonogramAvatar from '@/components/MonogramAvatar';
import {
  MapPin, Award, Calendar, CheckCircle, Star, Shield, MessageCircle, Phone,
  ArrowRight, Building2, Stethoscope, Globe, Sparkles, Heart, Clock, Download,
  ExternalLink, ChevronDown, Lock,
} from 'lucide-react';

const SECTION_TABS = [
  { id: 'highlights', label: 'Highlights' },
  { id: 'about', label: 'About' },
  { id: 'procedures', label: 'Procedures' },
  { id: 'locations', label: 'Locations' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'faqs', label: 'FAQs' },
];

export default function DoctorWebsiteMockA() {
  const { slug } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('highlights');
  const [scrolled, setScrolled] = useState(false);
  const [activeLoc, setActiveLoc] = useState(0);
  const heroRef = useRef(null);

  useEffect(() => {
    api.get(`/doctor/${slug}`).then(r => setDoctor(r.data)).finally(() => setLoading(false));
  }, [slug]);

  // Scroll listener — stick the mini-header once user passes the hero.
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 420);
      // scroll-spy
      const found = SECTION_TABS.find(t => {
        const el = document.getElementById(`sec-${t.id}`);
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return r.top <= 140 && r.bottom > 140;
      });
      if (found) setActiveTab(found.id);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTab = (id) => {
    const el = document.getElementById(`sec-${id}`);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  if (loading) return <FullScreenLoader />;
  if (!doctor) return <div className="min-h-screen flex items-center justify-center text-stone-500">Doctor not found</div>;

  const photoSrc = doctor.photo_url?.startsWith('/api/uploads')
    ? `${process.env.REACT_APP_BACKEND_URL || ''}${doctor.photo_url}`
    : doctor.photo_url;
  const primarySpec = (doctor.specialization_names || [])[0] || 'Doctor';
  const primaryLoc = (doctor.locations || [])[activeLoc] || (doctor.locations || [])[0];

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1C1917] antialiased" data-testid="mock-a-root">
      <Helmet>
        <title>{`[Mock A] Dr. ${doctor.name} — DocSite preview`}</title>
        <meta name="robots" content="noindex" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&family=Figtree:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      {/* PREVIEW BANNER */}
      <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-base text-center py-3 font-medium" data-testid="mock-a-preview-banner">
        Preview · Mock A · Editorial Warmth · This is the redesign mock — production page is at /dr/{slug}
      </div>

      {/* STICKY MINI-HEADER (Zocdoc-style) */}
      <header className={`sticky top-0 z-40 bg-[#FAF9F6]/95 backdrop-blur-md border-b transition-all duration-300 ${scrolled ? 'border-stone-200 shadow-[0_1px_0_rgba(0,0,0,0.04)]' : 'border-transparent'}`} data-testid="mock-a-header">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between gap-4">
          <Link to="/" className="font-semibold text-xl md:text-2xl tracking-tight" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Dr. {doctor.name?.replace(/^Dr\.?\s*/i, '')}
          </Link>
          {scrolled && (
            <div className="flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
              {photoSrc ? (
                <img src={photoSrc} alt="" className="w-11 h-11 rounded-full object-cover" />
              ) : (
                <div className="w-11 h-11"><MonogramAvatar name={doctor.name} /></div>
              )}
              <div className="hidden sm:flex flex-col text-right">
                <span className="text-base text-stone-500">{primarySpec}</span>
                {doctor.rating?.score && (
                  <span className="text-base font-semibold text-amber-700 flex items-center gap-1 justify-end">
                    <Star size={13} fill="currentColor" /> {doctor.rating.score}/5
                  </span>
                )}
              </div>
              <button onClick={() => scrollToTab('book')}
                className="rounded-full px-6 py-2.5 bg-[#7DA18B] hover:bg-[#6B8E78] text-white text-base font-semibold transition-all hover:scale-105"
                data-testid="mock-a-sticky-book-btn">
                View availability
              </button>
            </div>
          )}
          {!scrolled && (
            <nav className="hidden md:flex items-center gap-9 text-base text-stone-600">
              {SECTION_TABS.slice(0, 5).map(t => (
                <button key={t.id} onClick={() => scrollToTab(t.id)}
                  className="hover:text-stone-900 transition-colors">{t.label}</button>
              ))}
              <button onClick={() => scrollToTab('book')}
                className="rounded-full px-6 py-2.5 bg-[#7DA18B] hover:bg-[#6B8E78] text-white text-base font-semibold">
                Book Appointment
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* HERO — editorial confident */}
      <section ref={heroRef} className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-12 md:pt-20 pb-12 md:pb-16">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Text */}
            <div className="lg:col-span-7 space-y-6">
              {(doctor.specialization_names || []).length > 0 && (
                <div className="inline-flex items-center gap-2">
                  <span className="text-base md:text-lg uppercase tracking-[0.22em] font-bold text-[#7DA18B]">
                    {doctor.specialization_names.join(' · ')}
                  </span>
                </div>
              )}
              <h1
                className="font-light leading-[0.88] tracking-tight text-[#1C1917]"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 'clamp(5rem, 12vw, 13rem)',
                  letterSpacing: '-0.025em',
                }}
                data-testid="mock-a-hero-name"
              >
                {doctor.name}
              </h1>
              {doctor.tagline && (
                <p className="italic text-stone-600" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.6rem, 2.8vw, 2.4rem)' }}>
                  &ldquo;{doctor.tagline}&rdquo;
                </p>
              )}
              <p className="text-stone-700 max-w-2xl leading-[1.5]" style={{ fontFamily: 'Figtree, sans-serif', fontSize: 'clamp(1.35rem, 1.7vw, 1.65rem)' }}>
                {doctor.qualifications}
                {doctor.registration_number && (
                  <span className="block text-base md:text-lg text-stone-500 mt-3 uppercase tracking-widest">
                    NMC / State Medical Council Reg · {doctor.registration_number}
                  </span>
                )}
              </p>

              {/* Trust row — minimal, immediate */}
              <div className="flex flex-wrap gap-x-12 gap-y-3 pt-4 text-stone-600" style={{ fontFamily: 'Figtree, sans-serif', fontSize: 'clamp(1.15rem, 1.5vw, 1.4rem)' }}>
                <span className="flex items-center gap-2.5"><Award size={22} className="text-[#7DA18B]" /><strong className="text-stone-900 text-2xl md:text-3xl">{doctor.experience_years || 0}</strong>&nbsp;years experience</span>
                <span className="flex items-center gap-2.5"><Building2 size={22} className="text-[#7DA18B]" /><strong className="text-stone-900 text-2xl md:text-3xl">{(doctor.locations || []).length}</strong>&nbsp;clinic locations</span>
                <span className="flex items-center gap-2.5"><Stethoscope size={22} className="text-[#7DA18B]" /><strong className="text-stone-900 text-2xl md:text-3xl">{(doctor.procedures || []).length}</strong>&nbsp;procedures</span>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-3 pt-5">
                <button onClick={() => scrollToTab('book')}
                  className="inline-flex items-center gap-2 rounded-full px-9 py-4 bg-[#7DA18B] text-white text-lg md:text-xl font-semibold hover:bg-[#6B8E78] hover:scale-105 transition-all shadow-md shadow-[#7DA18B]/20"
                  data-testid="mock-a-hero-book-btn">
                  Book an appointment <ArrowRight size={20} />
                </button>
                <a href={`https://wa.me/${(doctor.phone || '').replace(/\D/g, '') || '910000000000'}`}
                   className="inline-flex items-center gap-2 rounded-full px-7 py-4 bg-white border border-stone-300 hover:border-stone-900 text-stone-900 text-lg md:text-xl font-semibold transition-colors"
                   data-testid="mock-a-hero-whatsapp-btn">
                  <MessageCircle size={20} /> WhatsApp Dr.
                </a>
                {doctor.consultation_fee && (
                  <span className="text-lg md:text-xl text-stone-500 ml-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    Consult fee · <strong className="text-stone-900">Rs. {doctor.consultation_fee}</strong>
                  </span>
                )}
              </div>
            </div>

            {/* Photo */}
            <div className="lg:col-span-5">
              <div className="relative">
                {photoSrc ? (
                  <div className="rounded-[28px] overflow-hidden shadow-2xl shadow-stone-300/40 max-h-[70vh] aspect-[4/5]">
                    <img src={photoSrc} alt={doctor.name}
                      className="w-full h-full object-cover"
                      style={{ filter: 'sepia(0.08) saturate(1.05) contrast(1.02)' }}
                    />
                  </div>
                ) : (
                  <div className="rounded-[28px] aspect-[4/5] bg-gradient-to-br from-[#D4C3B3]/60 via-[#FAF9F6] to-[#7DA18B]/20 flex items-center justify-center shadow-2xl shadow-stone-200/60">
                    <div className="w-2/3 max-w-[280px]">
                      <MonogramAvatar name={doctor.name} large />
                    </div>
                  </div>
                )}

                {/* Trained-at credential — hovers on bottom edge */}
                {doctor.trained_at?.hospital_name && (
                  <div className="absolute -bottom-8 left-4 right-4 mx-auto max-w-[420px] bg-white rounded-2xl shadow-xl shadow-stone-300/30 border border-stone-100 p-5"
                       data-testid="mock-a-trained-at-card">
                    <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-amber-700 leading-none flex items-center gap-1.5 mb-2">
                      <Award size={11} /> Trained at · Verified
                    </p>
                    <p className="text-base font-semibold text-stone-900 leading-snug" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.35rem', fontWeight: 500 }}>
                      {doctor.trained_at.hospital_name}
                      {doctor.trained_at.degree && (
                        <span className="text-sm font-normal text-stone-500 block mt-0.5">
                          {doctor.trained_at.degree}{doctor.trained_at.year ? ` · ${doctor.trained_at.year}` : ''}
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sage editorial ribbon — owned-and-portable strip */}
        <OwnershipStrip name={doctor.name} />
      </section>

      {/* SUB-NAV — scroll-spy tabs */}
      <div className="sticky top-20 z-30 bg-[#FAF9F6]/95 backdrop-blur-md border-b border-stone-200" data-testid="mock-a-subnav">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-center gap-10 overflow-x-auto scrollbar-hide h-16">
            {SECTION_TABS.map(t => (
              <button
                key={t.id}
                onClick={() => scrollToTab(t.id)}
                className={`relative whitespace-nowrap text-lg md:text-xl font-medium transition-colors ${activeTab === t.id ? 'text-stone-900' : 'text-stone-500 hover:text-stone-800'}`}
                style={{ fontFamily: 'Figtree, sans-serif' }}
                data-testid={`mock-a-tab-${t.id}`}
              >
                {t.label}
                {activeTab === t.id && (
                  <span className="absolute -bottom-[18px] left-0 right-0 h-[3px] bg-[#7DA18B]" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* MAIN GRID — content + sticky booking rail */}
      <main className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <div className="grid lg:grid-cols-3 gap-10 lg:gap-16">
          <div className="lg:col-span-2 space-y-20">
            {/* HIGHLIGHTS — 4 trust cards */}
            <Section id="highlights" eyebrow="At a glance" title="Highlights">
              <div className="grid sm:grid-cols-2 gap-5">
                <HighlightCard
                  icon={<Heart className="text-rose-500" size={26} />}
                  title="Patients return"
                  body="Repeat-visit rate above the city average for this specialty — patients trust the outcomes."
                />
                <HighlightCard
                  icon={<Sparkles className="text-amber-500" size={26} />}
                  title="Highly recommended"
                  body={doctor.rating?.score ? `${Math.round((doctor.rating.score / 5) * 100)}% of patients rated 4★ or above on bedside manner.` : 'Bedside-manner ratings will appear once 5+ verified reviews are in.'}
                />
                <HighlightCard
                  icon={<Clock className="text-sky-500" size={26} />}
                  title="On-time clinic"
                  body="Average wait under 20 minutes — schedules are honoured via DocSite's slot governance."
                />
                <HighlightCard
                  icon={<CheckCircle className="text-emerald-500" size={26} />}
                  title="New patients welcome"
                  body="Accepting new patients on every clinic day. Same-day slots open by 8 AM."
                />
              </div>
            </Section>

            {/* ABOUT — oversized */}
            <Section id="about" eyebrow={`About Dr. ${doctor.name?.replace(/^Dr\.?\s*/i, '').split(' ')[0]}`} title={`Why patients choose Dr. ${doctor.name?.replace(/^Dr\.?\s*/i, '').split(' ')[0]}`}>
              <p className="text-stone-700 leading-[1.7]" style={{ fontFamily: 'Figtree, sans-serif', fontSize: 'clamp(1.15rem, 1.5vw, 1.4rem)' }}>
                {doctor.bio || (
                  <span className="italic text-stone-400">Bio coming soon — Dr. {doctor.name} hasn't published their About section yet.</span>
                )}
              </p>
              {doctor.voice_average_pct >= 40 && (
                <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium" data-testid="mock-a-voice-badge">
                  <Sparkles size={14} /> Authored by Dr. {doctor.name} · Voice {doctor.voice_average_pct}%
                </div>
              )}
            </Section>

            {/* PROCEDURES */}
            <Section id="procedures" eyebrow="What I treat" title="Procedures & treatments">
              <div className="grid sm:grid-cols-2 gap-5">
                {(doctor.procedures || []).slice(0, 12).map((p, i) => (
                  <div key={p.id || i}
                       className="group rounded-2xl border border-stone-200 bg-white hover:border-[#7DA18B] hover:shadow-md hover:shadow-[#7DA18B]/10 transition-all p-6 cursor-pointer"
                       data-testid={`mock-a-proc-${i}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#7DA18B]/10 flex items-center justify-center shrink-0 group-hover:bg-[#7DA18B]/20">
                        <Stethoscope size={22} className="text-[#7DA18B]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-stone-900" style={{ fontFamily: 'Figtree, sans-serif', fontSize: 'clamp(1.25rem, 1.5vw, 1.5rem)' }}>{p.name}</p>
                        {(p.price_min || p.price_max) && (
                          <p className="text-stone-500 mt-1.5" style={{ fontSize: 'clamp(1rem, 1.15vw, 1.1rem)' }}>
                            {p.price_min && p.price_max && p.price_min !== p.price_max
                              ? `Rs. ${p.price_min.toLocaleString()} – ${p.price_max.toLocaleString()}`
                              : `Rs. ${(p.price_min || p.price_max || 0).toLocaleString()}`}
                          </p>
                        )}
                      </div>
                      <ArrowRight size={20} className="text-stone-300 group-hover:text-[#7DA18B] mt-2" />
                    </div>
                  </div>
                ))}
              </div>
              {(doctor.procedures || []).length > 12 && (
                <p className="text-stone-500 mt-6 text-lg">
                  + {(doctor.procedures || []).length - 12} more — <button className="text-[#7DA18B] font-semibold hover:underline">view full list</button>
                </p>
              )}
            </Section>

            {/* LOCATIONS */}
            <Section id="locations" eyebrow="Where to find me" title="Clinic locations">
              {(doctor.locations || []).length > 1 && (
                <div className="flex gap-2 mb-6 flex-wrap">
                  {doctor.locations.map((l, i) => (
                    <button key={l.id || i} onClick={() => setActiveLoc(i)}
                      className={`rounded-full px-5 py-2.5 text-base md:text-lg font-medium border transition-all ${activeLoc === i ? 'bg-[#1C1917] text-white border-[#1C1917]' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-500'}`}
                      data-testid={`mock-a-loc-tab-${i}`}>
                      {l.clinic_name || `Location ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}
              {primaryLoc && (
                <div className="rounded-3xl border border-stone-200 bg-white overflow-hidden">
                  <div className="aspect-[16/9] bg-stone-100 flex items-center justify-center text-stone-400">
                    <MapPin size={40} />
                    <span className="ml-3 text-lg">Map preview</span>
                  </div>
                  <div className="p-7 md:p-10 grid md:grid-cols-2 gap-7">
                    <div>
                      <p className="font-semibold text-stone-900" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.85rem, 2.4vw, 2.4rem)', fontWeight: 500 }}>
                        {primaryLoc.clinic_name}
                      </p>
                      <p className="text-stone-600 mt-3 leading-[1.6]" style={{ fontFamily: 'Figtree, sans-serif', fontSize: 'clamp(1.1rem, 1.3vw, 1.25rem)' }}>
                        {[primaryLoc.address, primaryLoc.city, primaryLoc.pincode].filter(Boolean).join(', ')}
                      </p>
                      <div className="flex gap-2 mt-5">
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([primaryLoc.address, primaryLoc.city].filter(Boolean).join(', '))}`}
                           target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-2 text-lg text-[#7DA18B] font-semibold hover:underline">
                          Get directions <ExternalLink size={16} />
                        </a>
                      </div>
                    </div>
                    <div>
                      <p className="uppercase tracking-widest font-bold text-stone-500 mb-3" style={{ fontSize: '0.95rem' }}>Clinic hours</p>
                      <p className="text-stone-700 leading-relaxed" style={{ fontSize: 'clamp(1.1rem, 1.3vw, 1.25rem)' }}>Mon–Sat · 10:00 AM – 7:30 PM<br/>Sunday closed</p>
                    </div>
                  </div>
                </div>
              )}
            </Section>

            {/* REVIEWS */}
            <Section id="reviews" eyebrow="What patients say" title="Patient reviews">
              <div className="grid grid-cols-3 gap-7 mb-10 pb-10 border-b border-stone-200">
                <RatingBar label="Overall rating" score={doctor.rating?.score || 4.8} />
                <RatingBar label="Wait time" score={4.6} />
                <RatingBar label="Bedside manner" score={4.9} />
              </div>
              <div className="rounded-xl bg-stone-50 border border-stone-200 p-5 flex items-start gap-3 text-stone-700 mb-7" style={{ fontSize: 'clamp(1.05rem, 1.2vw, 1.15rem)' }}>
                <Shield size={20} className="text-stone-500 shrink-0 mt-0.5" />
                <p>Reviews are submitted only after a verified booked consultation. Doctors cannot pay to alter or remove reviews.</p>
              </div>
              <div className="space-y-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="rounded-2xl border border-stone-200 bg-white p-7" data-testid={`mock-a-review-${i}`}>
                    <div className="flex items-center gap-1.5 mb-3">
                      {[1,2,3,4,5].map(s => <Star key={s} size={18} className="text-amber-400" fill="currentColor" />)}
                    </div>
                    <p className="text-stone-800 leading-[1.55]" style={{ fontFamily: 'Figtree, sans-serif', fontSize: 'clamp(1.15rem, 1.4vw, 1.35rem)' }}>
                      &ldquo;Dr. {doctor.name?.replace(/^Dr\.?\s*/i, '').split(' ')[0]} took the time to explain everything and didn&apos;t rush. Highly recommend.&rdquo;
                    </p>
                    <p className="text-stone-500 mt-4" style={{ fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)' }}>Verified patient · {['Feb 3, 2026', 'Jan 28, 2026', 'Dec 15, 2025'][i - 1]}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* FAQs */}
            <Section id="faqs" eyebrow="Common questions" title="Patient FAQs">
              <div className="space-y-4">
                {[
                  { q: 'Do you accept walk-ins?', a: `For best service please book a slot via the form on the right — Dr. ${doctor.name?.replace(/^Dr\.?\s*/i, '').split(' ')[0]} keeps daily walk-in capacity but it can fill quickly.` },
                  { q: 'Are video consults available?', a: 'Yes — both in-person and video visits are bookable through this page.' },
                  { q: 'Can I export my visit history?', a: 'Yes. Your visit history is yours, exportable as PDF any time from your patient profile.' },
                ].map((f, i) => (
                  <details key={i} className="group rounded-2xl border border-stone-200 bg-white px-7 py-6 cursor-pointer" data-testid={`mock-a-faq-${i}`}>
                    <summary className="flex items-center justify-between font-semibold text-stone-900 list-none" style={{ fontFamily: 'Figtree, sans-serif', fontSize: 'clamp(1.2rem, 1.5vw, 1.45rem)' }}>
                      {f.q}
                      <ChevronDown size={22} className="text-stone-400 group-open:rotate-180 transition-transform" />
                    </summary>
                    <p className="text-stone-600 leading-[1.65] mt-4" style={{ fontSize: 'clamp(1.1rem, 1.3vw, 1.25rem)' }}>{f.a}</p>
                  </details>
                ))}
              </div>
            </Section>

            {/* OWNERSHIP STRIP — full long form */}
            <OwnershipManifesto name={doctor.name} />
          </div>

          {/* STICKY BOOKING CARD (right rail) */}
          <aside className="lg:col-span-1">
            <div id="sec-book" className="lg:sticky lg:top-32 space-y-4" data-testid="mock-a-booking-card">
              <div className="rounded-3xl border-2 border-[#1C1917] bg-white p-7 md:p-8 shadow-xl shadow-stone-300/30">
                <p className="uppercase tracking-[0.22em] font-bold text-[#7DA18B] mb-3" style={{ fontSize: '0.95rem' }}>Direct booking · No middleman</p>
                <h3 className="text-stone-900 leading-[1.05] mt-1" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 2.6vw, 2.6rem)', fontWeight: 500 }}>
                  Book an appointment with Dr. {doctor.name?.replace(/^Dr\.?\s*/i, '').split(' ')[0]}
                </h3>
                <p className="text-stone-500 mt-4" style={{ fontSize: 'clamp(1.05rem, 1.2vw, 1.2rem)' }}>No platform fee. Slots confirm instantly.</p>

                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <button className="rounded-xl border-2 border-[#7DA18B] bg-[#7DA18B]/5 px-5 py-4 font-semibold text-[#1C1917] flex items-center justify-center gap-2" style={{ fontSize: 'clamp(1rem, 1.15vw, 1.15rem)' }}>
                      <Calendar size={18} /> In-person
                    </button>
                    <button className="rounded-xl border border-stone-300 bg-white px-5 py-4 font-medium text-stone-600 hover:border-[#7DA18B]" style={{ fontSize: 'clamp(1rem, 1.15vw, 1.15rem)' }}>
                      Video visit
                    </button>
                  </div>

                  <div className="rounded-xl border border-stone-200 bg-stone-50 px-5 py-4">
                    <p className="uppercase tracking-widest font-bold text-stone-500" style={{ fontSize: '0.9rem' }}>Today, Feb 9</p>
                    <p className="font-semibold text-stone-900 mt-2" style={{ fontSize: 'clamp(1.15rem, 1.4vw, 1.35rem)' }}>3 slots open · Earliest 4:30 PM</p>
                  </div>

                  <button className="w-full rounded-full bg-[#1C1917] hover:bg-stone-800 text-white py-4 font-semibold transition-colors" style={{ fontSize: 'clamp(1.1rem, 1.3vw, 1.25rem)' }}>
                    See all available slots →
                  </button>
                </div>

                <div className="border-t border-stone-200 pt-5 mt-6 space-y-2.5 text-stone-500" style={{ fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)' }}>
                  <p className="flex items-center gap-2.5"><Phone size={15} /> Direct call · {doctor.phone || 'On request'}</p>
                  <p className="flex items-center gap-2.5"><MessageCircle size={15} /> WhatsApp confirmed within 1 hour</p>
                </div>
              </div>

              {/* Trust mini-strip */}
              <div className="rounded-2xl bg-gradient-to-br from-[#7DA18B]/10 to-[#D4C3B3]/20 border border-[#7DA18B]/20 p-6">
                <div className="flex items-start gap-3">
                  <Shield size={26} className="text-[#7DA18B] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-stone-900 leading-snug" style={{ fontSize: 'clamp(1.1rem, 1.3vw, 1.25rem)' }}>This site belongs to Dr. {doctor.name?.replace(/^Dr\.?\s*/i, '').split(' ')[0]}</p>
                    <p className="text-stone-600 leading-[1.55] mt-2" style={{ fontSize: 'clamp(0.95rem, 1.1vw, 1.1rem)' }}>
                      Hosted by DocSite. Doctor-owned data. Exportable any day.
                      <Link to="/owned-by-doctor" className="text-[#7DA18B] font-semibold hover:underline ml-1">Why this matters →</Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-stone-200 bg-[#FAF9F6] mt-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-14 grid md:grid-cols-3 gap-8 text-stone-500">
          <div>
            <p className="uppercase tracking-[0.22em] font-bold text-stone-700 mb-3" style={{ fontSize: '0.95rem' }}>Owned by Dr. {doctor.name?.replace(/^Dr\.?\s*/i, '').split(' ')[0]}</p>
            <p className="leading-[1.6]" style={{ fontSize: 'clamp(1.05rem, 1.2vw, 1.2rem)' }}>This is Dr. {doctor.name?.replace(/^Dr\.?\s*/i, '').split(' ')[0]}'s personal medical practice website. All content, patient data, and bookings belong to them. Hosted on the DocSite platform.</p>
          </div>
          <div>
            <p className="uppercase tracking-[0.22em] font-bold text-stone-700 mb-3" style={{ fontSize: '0.95rem' }}>Patient privacy</p>
            <p className="leading-[1.6]" style={{ fontSize: 'clamp(1.05rem, 1.2vw, 1.2rem)' }}>No tracking pixels. No data brokers. Your visit history is yours — exportable any time as a PDF.</p>
          </div>
          <div>
            <p className="uppercase tracking-[0.22em] font-bold text-stone-700 mb-3" style={{ fontSize: '0.95rem' }}>Powered by</p>
            <Link to="/" className="font-semibold text-stone-900" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.85rem' }}>DocSite</Link>
            <p className="text-stone-400 mt-2" style={{ fontSize: '1.05rem' }}>The portable doctor website platform · India</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============== Helpers ==============

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
      <div className="text-stone-400" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem' }}>
        Loading…
      </div>
    </div>
  );
}

function Section({ id, eyebrow, title, children }) {
  return (
    <section id={`sec-${id}`} className="scroll-mt-40" data-testid={`mock-a-section-${id}`}>
      <div className="mb-12">
        <p className="uppercase tracking-[0.24em] font-bold text-[#7DA18B] mb-5" style={{ fontSize: 'clamp(1rem, 1.3vw, 1.35rem)' }}>{eyebrow}</p>
        <h2
          className="font-light leading-[0.95] tracking-tight text-[#1C1917]"
          style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 'clamp(3.25rem, 6.5vw, 6.75rem)',
            letterSpacing: '-0.02em',
          }}
        >
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function HighlightCard({ icon, title, body }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-7 md:p-8 hover:border-stone-400 hover:-translate-y-0.5 transition-all" data-testid={`mock-a-highlight-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-14 h-14 rounded-xl bg-stone-50 flex items-center justify-center mb-4">{icon}</div>
      <p className="font-semibold text-stone-900" style={{ fontFamily: 'Figtree, sans-serif', fontSize: 'clamp(1.5rem, 1.9vw, 1.85rem)' }}>{title}</p>
      <p className="text-stone-600 mt-3 leading-[1.55]" style={{ fontSize: 'clamp(1.05rem, 1.25vw, 1.2rem)' }}>{body}</p>
    </div>
  );
}

function RatingBar({ label, score }) {
  return (
    <div data-testid={`mock-a-rating-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="font-semibold text-stone-900" style={{ fontSize: 'clamp(1.05rem, 1.25vw, 1.2rem)' }}>{label}</p>
      <div className="flex items-center gap-1.5 mt-3">
        {[1,2,3,4,5].map(s => (
          <Star key={s} size={20} className={s <= Math.round(score) ? 'text-amber-400' : 'text-stone-300'} fill={s <= Math.round(score) ? 'currentColor' : 'none'} />
        ))}
        <span className="ml-2 text-2xl md:text-3xl font-bold text-stone-900 tabular-nums">{score}</span>
      </div>
    </div>
  );
}

function OwnershipStrip({ name }) {
  return (
    <div className="bg-[#7DA18B] text-[#FAF9F6]" data-testid="mock-a-ownership-strip">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-7 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-center">
        <span className="inline-flex items-center gap-2.5 font-semibold tracking-wide" style={{ fontFamily: 'Figtree, sans-serif', fontSize: 'clamp(1.05rem, 1.25vw, 1.25rem)' }}>
          <Lock size={18} /> Owned by Dr. {name?.replace(/^Dr\.?\s*/i, '').split(' ')[0]}
        </span>
        <span className="hidden sm:inline text-[#FAF9F6]/40">·</span>
        <span className="inline-flex items-center gap-2.5 font-medium" style={{ fontFamily: 'Figtree, sans-serif', fontSize: 'clamp(1.05rem, 1.25vw, 1.25rem)' }}>
          <Download size={18} /> Portable forever — export any day
        </span>
        <span className="hidden sm:inline text-[#FAF9F6]/40">·</span>
        <span className="inline-flex items-center gap-2.5 font-medium" style={{ fontFamily: 'Figtree, sans-serif', fontSize: 'clamp(1.05rem, 1.25vw, 1.25rem)' }}>
          <Globe size={18} /> Hosted by DocSite · No middleman
        </span>
      </div>
    </div>
  );
}

function OwnershipManifesto({ name }) {
  const first = name?.replace(/^Dr\.?\s*/i, '').split(' ')[0] || 'Doctor';
  return (
    <section className="rounded-3xl bg-gradient-to-br from-[#7DA18B]/8 via-[#FAF9F6] to-[#D4C3B3]/30 border border-[#7DA18B]/20 p-8 md:p-12" data-testid="mock-a-ownership-manifesto">
      <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#7DA18B] mb-4">The DocSite difference</p>
      <h2
        className="font-light leading-[0.95] tracking-tight text-[#1C1917] mb-6"
        style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.75rem, 5.5vw, 5rem)' }}
      >
        This isn't a directory listing. <br className="hidden md:block" />
        <span className="italic text-[#7DA18B]">It's Dr. {first}'s website.</span>
      </h2>
      <p className="text-stone-700 leading-[1.7] max-w-2xl text-[1.15rem] md:text-[1.25rem]" style={{ fontFamily: 'Figtree, sans-serif' }}>
        Every word, every photo, every booking, every patient relationship on this page belongs to Dr. {first}.
        DocSite hosts it — but the data is portable: domain, content, history, and patient list can be exported in one click, any day.
        Unlike platforms where you rent a page, here Dr. {first} owns the entire address.
      </p>
      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        {[
          { icon: <Lock size={20} className="text-[#7DA18B]" />, title: 'Doctor-owned', body: 'Content, brand, and patient data — held by the doctor, not the platform.' },
          { icon: <Download size={20} className="text-[#7DA18B]" />, title: 'Always portable', body: 'One-click PDF + JSON export of profile, schedules, and patient history.' },
          { icon: <Shield size={20} className="text-[#7DA18B]" />, title: 'No tracking', body: 'No data brokers, no third-party pixels. HTTPS-only. Patient privacy enforced.' },
        ].map(c => (
          <div key={c.title} className="rounded-2xl bg-white border border-stone-200 p-5">
            <div className="mb-2">{c.icon}</div>
            <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Figtree, sans-serif' }}>{c.title}</p>
            <p className="text-xs text-stone-600 leading-relaxed mt-1">{c.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
